import { AuthContext } from '@urbanx/agx.auth.client';
import { useContext, useEffect, useRef, useState } from 'react';
import {
  ContractActionRequestContractAction,
  DocumentTypes,
  StaffGetContractSettlementResponseSettlementType as SettlementType,
  StaffUpdateContractSettlementRequestSettlementType,
  type StaffUpdateContractSettlementRequest,
  UploadedDocument,
  useInvalidateUseStaffGetContract,
  useInvalidateUseStaffGetContractSettlement,
  usePerformContractAction,
  useStaffGetContractSettlement,
  useStaffUpdateContractSettlement,
} from '@urbanx/agx.campaigns.client';
import {
  TaskBlockerBlockerType,
  useInvalidateUseGetAutopilotTasksForCampaign,
  useReconcileSettlementTask,
} from '@urbanx/agx.activities.client';
import { AxiosProgressEvent, GenericAbortSignal } from 'axios';
import { uploadFile } from 'services';
import { toDateOnlyString, useToast } from '@urbanx/agx.ui-components2';
import {
  buildSettlementContractDocuments,
  getInitialSettlementDate,
  hasDocumentSelectionChanged,
} from './settlementFormState';

interface Props {
  dialogOpenChangeHandler: (value: boolean) => void;
  taskId: string;
  contractId?: string;
  campaignId?: string;
  isOpen: boolean;
  onTasksInvalidated?: () => void | Promise<void>;
}

const isFile = (file: UploadedDocument | File | undefined): file is File =>
  typeof File !== 'undefined' && file instanceof File;

export const useMarkedAsSettled = ({
  taskId,
  contractId,
  campaignId,
  isOpen,
  dialogOpenChangeHandler,
  onTasksInvalidated,
}: Props) => {
  const { staffUpdateContractSettlement } = useStaffUpdateContractSettlement({
    onSuccess: () => {},
    onError: () => {},
  });
  const { getAuthToken } = useContext(AuthContext);
  const { pushToast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAwaitingTaskReconciliation, setIsAwaitingTaskReconciliation] = useState(false);
  const submissionInFlight = useRef(false);
  const contractMarkedAsSettled = useRef(false);
  const invalidateTasks = useInvalidateUseGetAutopilotTasksForCampaign();
  const invalidateContract = useInvalidateUseStaffGetContract();
  const invalidateContractSettlement = useInvalidateUseStaffGetContractSettlement();
  const { performContractAction } = usePerformContractAction({
    onSuccess: () => {},
    onError: () => {},
  });
  const { reconcileSettlementTask } = useReconcileSettlementTask({
    onSuccess: () => {},
    onError: () => {},
  });
  const { data: settlement, isPending: settlementPending } = useStaffGetContractSettlement(
    campaignId ?? '',
    contractId ?? '',
    !!campaignId && !!contractId
  );
  const [settlementDate, setSettlementDate] = useState<Date | null>(null);
  const [settlementType, setSettlementType] = useState(SettlementType.Pexa);
  const [vendorConfirmationFiles, setVendorConfirmationFiles] = useState<
    (UploadedDocument | File)[]
  >([]);
  const [buyerConfirmationFiles, setBuyerConfirmationFiles] = useState<(UploadedDocument | File)[]>(
    []
  );
  const [pexaConfirmationFiles, setPexaConfirmationFiles] = useState<(UploadedDocument | File)[]>(
    []
  );
  const [additionalCorrespondenceFiles, setAdditionalCorrespondenceFiles] = useState<
    (UploadedDocument | File)[]
  >([]);
  const [haveAllSuppliersBeenPaid, setHaveAllSuppliersBeenPaid] = useState<boolean>();
  const [isFormReady, setIsFormReady] = useState(false);
  const formInitialized = useRef(false);

  const buildRenamedFormData = (formData: FormData) => {
    const file = formData.get('File');
    if (!file || typeof File === 'undefined' || !(file instanceof File)) {
      return formData;
    }

    const renamedFile = new File([file], `${contractId}/${file.name}`, {
      type: file.type,
      lastModified: file.lastModified,
    });
    const renamedFormData = new FormData();
    renamedFormData.append('File', renamedFile);
    return renamedFormData;
  };

  const onUploadFile = async (
    authToken: string,
    file: string | FormData,
    uploadCampaignId: string,
    uploadedFormType: DocumentTypes,
    uploadProgress?: (progressEvent: AxiosProgressEvent) => void,
    abortSignal?: GenericAbortSignal,
    uploadContractId?: string
  ) =>
    uploadFile(
      authToken,
      typeof FormData !== 'undefined' && file instanceof FormData
        ? buildRenamedFormData(file)
        : file,
      uploadCampaignId,
      uploadedFormType,
      uploadProgress ?? null,
      abortSignal ?? null,
      undefined,
      uploadContractId
    );

  useEffect(() => {
    if (!isOpen) {
      formInitialized.current = false;
      setIsFormReady(false);
      return;
    }
    if (settlementPending || formInitialized.current) return;

    formInitialized.current = true;

    setSettlementDate(getInitialSettlementDate(settlement?.actualSettlementDate));
    setSettlementType(settlement?.settlementType ?? SettlementType.Pexa);
    setVendorConfirmationFiles(
      settlement?.vendorSettlementConfirmation ? [settlement.vendorSettlementConfirmation] : []
    );
    setBuyerConfirmationFiles(
      settlement?.buyerSettlementConfirmation ? [settlement.buyerSettlementConfirmation] : []
    );
    setPexaConfirmationFiles(settlement?.pexaSettlementConfirmations ?? []);
    setAdditionalCorrespondenceFiles(settlement?.additionalSettlementCorrespondence ?? []);
    setHaveAllSuppliersBeenPaid(settlement?.haveAllSuppliersBeenPaid);
    setIsFormReady(true);
  }, [isOpen, settlement, settlementPending]);

  const hasRequiredConfirmation =
    settlementType === SettlementType.Pexa
      ? pexaConfirmationFiles.length > 0
      : vendorConfirmationFiles.length > 0 && buyerConfirmationFiles.length > 0;
  const canComplete =
    settlementDate != null && hasRequiredConfirmation && haveAllSuppliersBeenPaid !== undefined;

  const uploadSelectedFile = async (
    authToken: string,
    documentType: DocumentTypes,
    selectedFile: File
  ) => {
    if (!campaignId) throw new Error('Campaign is required to upload a settlement confirmation');

    const formData = new FormData();
    formData.append('File', selectedFile);
    const uploadedDocument = await onUploadFile(
      authToken,
      formData,
      campaignId,
      documentType,
      undefined,
      undefined,
      contractId
    );
    if (!uploadedDocument) {
      throw new Error(`Failed to upload ${documentType}`);
    }

    return uploadedDocument as UploadedDocument;
  };

  const uploadPendingFiles = async (
    authToken: string | undefined,
    files: (UploadedDocument | File)[],
    documentType: DocumentTypes,
    setFiles: (files: (UploadedDocument | File)[]) => void
  ): Promise<UploadedDocument[]> => {
    const uploadedDocuments: UploadedDocument[] = [];

    for (const file of files) {
      if (!isFile(file)) {
        uploadedDocuments.push(file);
        continue;
      }

      if (!authToken) throw new Error('Unable to authenticate confirmation upload');

      uploadedDocuments.push(await uploadSelectedFile(authToken, documentType, file));
      setFiles([...uploadedDocuments, ...files.slice(uploadedDocuments.length)]);
    }

    return uploadedDocuments;
  };

  const persistSettlementChanges = async (
    refreshWhenUnchanged = false,
    markContractAsSettled = false
  ) => {
    if (!campaignId || !contractId || submissionInFlight.current) return;

    submissionInFlight.current = true;
    setIsSubmitting(true);
    let submissionStep: 'details' | 'upload' | 'settlement' | 'reconciliation' = 'details';

    try {
      if (markContractAsSettled && contractMarkedAsSettled.current) {
        submissionStep = 'reconciliation';
        await reconcileTask(campaignId, contractId, taskId);
        await refreshSettledContract(campaignId, contractId);
        dialogOpenChangeHandler(false);
        clearSelection();
        return;
      }

      const selectedSettlementDate = toDateOnlyString(settlementDate);
      const currentSettlementDate = toDateOnlyString(settlement?.actualSettlementDate);
      const settlementDateDirty = selectedSettlementDate !== currentSettlementDate;
      const settlementTypeDirty = settlementType !== settlement?.settlementType;
      const haveAllSuppliersBeenPaidDirty =
        haveAllSuppliersBeenPaid !== settlement?.haveAllSuppliersBeenPaid;

      const pendingUploadCount = [
        ...vendorConfirmationFiles,
        ...buyerConfirmationFiles,
        ...pexaConfirmationFiles,
        ...additionalCorrespondenceFiles,
      ].filter(isFile).length;

      const settlementDocumentsChanged =
        hasDocumentSelectionChanged(
          vendorConfirmationFiles,
          settlement?.vendorSettlementConfirmation ? [settlement.vendorSettlementConfirmation] : []
        ) ||
        hasDocumentSelectionChanged(
          buyerConfirmationFiles,
          settlement?.buyerSettlementConfirmation ? [settlement.buyerSettlementConfirmation] : []
        ) ||
        hasDocumentSelectionChanged(
          pexaConfirmationFiles,
          settlement?.pexaSettlementConfirmations ?? []
        ) ||
        hasDocumentSelectionChanged(
          additionalCorrespondenceFiles,
          settlement?.additionalSettlementCorrespondence ?? []
        );
      const hasChanges =
        settlementDateDirty ||
        settlementTypeDirty ||
        haveAllSuppliersBeenPaidDirty ||
        settlementDocumentsChanged;
      const authToken = pendingUploadCount > 0 ? await getAuthToken() : undefined;
      if (pendingUploadCount > 0 && !authToken) {
        throw new Error('Unable to authenticate confirmation upload');
      }

      if (pendingUploadCount > 0) {
        submissionStep = 'upload';
      }

      const uploadResults = await Promise.allSettled([
        uploadPendingFiles(
          authToken,
          vendorConfirmationFiles,
          DocumentTypes.VendorSettlementConfirmation,
          setVendorConfirmationFiles
        ),
        uploadPendingFiles(
          authToken,
          buyerConfirmationFiles,
          DocumentTypes.BuyerSettlementConfirmation,
          setBuyerConfirmationFiles
        ),
        uploadPendingFiles(
          authToken,
          pexaConfirmationFiles,
          DocumentTypes.PexaSettlementConfirmation,
          setPexaConfirmationFiles
        ),
        uploadPendingFiles(
          authToken,
          additionalCorrespondenceFiles,
          DocumentTypes.AdditionalSettlementCorrespondence,
          setAdditionalCorrespondenceFiles
        ),
      ]);
      const [
        canonicalVendorConfirmations,
        canonicalBuyerConfirmations,
        canonicalPexaConfirmations,
        canonicalAdditionalCorrespondence,
      ] = uploadResults.map((result) => {
        if (result.status === 'rejected') throw result.reason;
        return result.value;
      });

      if (hasChanges) {
        submissionStep = 'details';
        const body: StaffUpdateContractSettlementRequest = {};
        if (settlementDateDirty) body.actualSettlementDate = selectedSettlementDate;
        if (settlementTypeDirty) {
          body.settlementType =
            settlementType === SettlementType.Pexa
              ? StaffUpdateContractSettlementRequestSettlementType.Pexa
              : StaffUpdateContractSettlementRequestSettlementType.InPerson;
        }
        if (haveAllSuppliersBeenPaidDirty) {
          body.haveAllSuppliersBeenPaid = haveAllSuppliersBeenPaid;
        }
        if (settlementDocumentsChanged) {
          body.contractDocuments = buildSettlementContractDocuments({
            vendorConfirmation: canonicalVendorConfirmations[0],
            buyerConfirmation: canonicalBuyerConfirmations[0],
            pexaConfirmations: canonicalPexaConfirmations,
            additionalSettlementCorrespondence: canonicalAdditionalCorrespondence,
          });
        }
        await staffUpdateContractSettlement({ campaignId, contractId, body });
      }

      if (hasChanges || refreshWhenUnchanged) {
        await invalidateContractSettlement(campaignId, contractId);
        if (!markContractAsSettled) {
          if (settlementDocumentsChanged) await invalidateContract(contractId);
          await invalidateTasks(campaignId, contractId);
          await onTasksInvalidated?.();
        }
      }

      if (markContractAsSettled) {
        submissionStep = 'settlement';
        await performContractAction({
          campaignId,
          contractId,
          contractAction: ContractActionRequestContractAction.MarkSettled,
        });
        contractMarkedAsSettled.current = true;
        setIsAwaitingTaskReconciliation(true);
        submissionStep = 'reconciliation';
        await reconcileTask(campaignId, contractId, taskId);

        try {
          await refreshSettledContract(campaignId, contractId);
        } catch {
          pushToast({
            type: 'error',
            message:
              'The contract was marked as settled, but the latest status could not be loaded. Refresh the page to see the update.',
          });
        }
      }

      dialogOpenChangeHandler(false);
      clearSelection();
    } catch {
      const message =
        submissionStep === 'upload'
          ? 'The settlement confirmation could not be uploaded. Check the file and try again.'
          : submissionStep === 'reconciliation'
            ? 'The contract was marked as settled, but the task blockers could not be updated. Try the task update again.'
            : submissionStep === 'settlement'
              ? 'The contract could not be marked as settled. Your settlement details have been kept so you can try again.'
              : 'The settlement details could not be saved. Check your connection and try again.';
      pushToast({ type: 'error', message });
    } finally {
      submissionInFlight.current = false;
      setIsSubmitting(false);
    }
  };

  const reconcileTask = async (campaignId: string, contractId: string, taskId: string) => {
    const updatedTask = await reconcileSettlementTask({ campaignId, contractId, taskId });
    if (
      updatedTask.blockers?.some(
        (blocker) => blocker.blockerType === TaskBlockerBlockerType.NotMarkedAsSettled
      )
    ) {
      throw new Error('Settlement task reconciliation did not remove the blocker');
    }
  };

  const refreshSettledContract = async (campaignId: string, contractId: string) => {
    await invalidateContract(contractId);
    await invalidateTasks(campaignId, contractId);
    await onTasksInvalidated?.();
  };

  const onComplete = async () => {
    if (isSubmitting || !canComplete) return;
    await persistSettlementChanges(true, true);
  };

  const onSaveAndExit = async () => {
    if (contractMarkedAsSettled.current) return;
    await persistSettlementChanges();
  };

  const clearSelection = () => {
    contractMarkedAsSettled.current = false;
    setIsAwaitingTaskReconciliation(false);
    setSettlementDate(null);
    setSettlementType(SettlementType.Pexa);
    setVendorConfirmationFiles([]);
    setBuyerConfirmationFiles([]);
    setPexaConfirmationFiles([]);
    setAdditionalCorrespondenceFiles([]);
    setHaveAllSuppliersBeenPaid(undefined);
  };

  return {
    getAuthToken,
    onUploadFile,
    settlementDate,
    setSettlementDate,
    settlementType,
    setSettlementType,
    vendorConfirmationFiles,
    setVendorConfirmationFiles,
    buyerConfirmationFiles,
    setBuyerConfirmationFiles,
    pexaConfirmationFiles,
    setPexaConfirmationFiles,
    additionalCorrespondenceFiles,
    setAdditionalCorrespondenceFiles,
    haveAllSuppliersBeenPaid,
    setHaveAllSuppliersBeenPaid,
    isSubmitting,
    isAwaitingTaskReconciliation,
    isFormReady,
    canComplete,
    onComplete,
    clearSelection,
    onSaveAndExit,
  };
};
